import { HttpClient } from '@angular/common/http';
import { Component, ViewChild } from '@angular/core';
import { DialogOpenEventArgs, DialogService, PopupOpenEventArgs } from './app.dialog-service';
import { Language, LANGUAGES, LANGUAGE_ABBREVIATION, LANGUAGE_IMAGES, TranslationService } from './app.translation-service';
import { DropdownOption } from './dropdown/dropdown.component';
import { FooterComponent } from './footer/footer.component';

/**
 * SiteContentItemType
 */
export enum SiteContentItemType {
	Dark = 0,
	Standard = 1,
	Milk = 2,
	White = 3
}

/**
 * SiteContentItem
 */
export interface SiteContentItem {
	id: number;
	name: string;
	image?: string;
	content?: string;
	type: SiteContentItemType;
	subContent?: SiteContentItem[];
}

/**
 * CONFIG_KEY_COOKIES_MESSAGE
 */
const CONFIG_KEY_COOKIES_MESSAGE = 'cookies-message-displayed';

/**
 * MINUTES_TO_CLOSE
 */
const MINUTES_TO_CLOSE = 5;

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.less']
})
export class AppComponent {

	@ViewChild(FooterComponent)
	public footer?: FooterComponent;

	/**
	 * Selected option time out
	 */
	private selectedOptionTimeout: any;

	/**
	 * Portrait mode
	 */
	public portraitMode = false;

	/**
	 * Site content
	 */
	public siteContent?: SiteContentItem[] | null = null;

	/**
	 * Selected option
	 */
	public selectedOption?: number | null;

	/**
	 * Highlighted option
	 */
	public highlightedOption?: number | null;

	/**
	 * Selected option info
	 */
	public selectedOptionInfo?: {selected?: number | null};

	/**
	 * Selected option content
	 */
	public selectedOptionContent?: SiteContentItem | null;

	/**
	 * Language options
	 */
	public languageOptions: DropdownOption[];

	/**
	 * Selected language
	 */
	public selectedLanguage: Language;

	/**
	 * Dialog title
	 */
	 public dialogTitle: string | null = null;

	/**
	 * Dialog content
	 */
	public dialogContent: string | null = null;

	/**
	 * Popup args
	 */
	public popupArgs: PopupOpenEventArgs | null = null;

	/**
	 * Portrait mode menu opened
	 */
	public portraitModeMenuOpened = false;

	/**
	 * Site content
	 */
	constructor(public i18n: TranslationService, public dialog: DialogService, private http: HttpClient) {
		const languageOptions = [];
		for (const l of LANGUAGES) {
			languageOptions.push({
				id: l,
				image: LANGUAGE_IMAGES[l]
			});
		}
		this.languageOptions = languageOptions;

		this.selectedLanguage = this.i18n.language;

		this.dialog.openDialogEvent.subscribe((args: DialogOpenEventArgs): void => {
			if (args == null) {
				this.dialogTitle = null;
				this.dialogContent = null;
			} else {
				this.dialogTitle = args.title;
				this.dialogContent = args.content;
			}
		});

		this.dialog.openPopupEvent.subscribe((args: PopupOpenEventArgs): void => {
			this.popupArgs = args;
		});

		// fill site content
		this.http.get('assets/content/index.json').subscribe((data: any) => {
			const siteContent = [];

			//#region level 0
			for (const category of data.items) {
				const categoryObject: SiteContentItem = {
					id: category.id,
					name: '',
					type: 0,
					subContent: []
				};

				if (category.type === 'dark') {
					categoryObject.type = SiteContentItemType.Dark;
				} else if (category.type === 'standard') {
					categoryObject.type = SiteContentItemType.Standard;
				} else if (category.type === 'milk') {
					categoryObject.type = SiteContentItemType.Milk;
				} else if (category.type === 'white') {
					categoryObject.type = SiteContentItemType.White;
				}

				categoryObject.name = category.name[LANGUAGE_ABBREVIATION[this.selectedLanguage]];

				//#region level 1
				let i = 0;
				let classe;
				for (const categoryClass of category.items) {
					if (typeof(categoryClass) === 'number') {
						classe = (data.classes as any[]).find(c => c.id === Number(categoryClass));
					} else {
						classe = categoryClass;
					}
					const categoryClassObject: SiteContentItem = {
						id: (category.id * 10) + i,
						name: '',
						image: classe.image,
						type: 0
					};

					categoryClassObject.name = classe.name[LANGUAGE_ABBREVIATION[this.selectedLanguage]];

					//#region level 2
					let j = 0;
					categoryClassObject.subContent = [];
					for (const categoryClassItem of classe.content) {
						const categoryClassItemObject: SiteContentItem = {
							id: (categoryClassObject.id * 10) + j,
							name: '',
							image: categoryClassItem.image,
							type: 0
						};

						categoryClassItemObject.name = categoryClassItem.name[LANGUAGE_ABBREVIATION[this.selectedLanguage]];

						// prepare final content
						const content = categoryClassItem.content[LANGUAGE_ABBREVIATION[this.selectedLanguage]];

						if (typeof(content) === 'string') {
							categoryClassItemObject.content = content;
						} else if (content instanceof Array) {
							categoryClassItemObject.content = '<ul>';
							for (const contentItem of content) {
								categoryClassItemObject.content += '<li>' + contentItem + '</li>';
							}
							categoryClassItemObject.content += '</ul>';
						}

						categoryClassObject.subContent?.push(categoryClassItemObject);

						j++;
					}
					//#endregion

					categoryObject.subContent?.push(categoryClassObject);

					i++;
				}
				//#endregion

				siteContent.push(categoryObject);
			}
			//#endregion

			this.siteContent = siteContent;
		});

		window.addEventListener('resize', this.onWindowResize);

		this.onWindowResize();

		setTimeout(() => {
			const cookiesMessage: string | Language | null = localStorage.getItem(CONFIG_KEY_COOKIES_MESSAGE);

			if (cookiesMessage == null) {
				this.openCookiesPopup();

				localStorage.setItem(CONFIG_KEY_COOKIES_MESSAGE, 'true');
			}
		});
	}

	/**
	 * On window resize
	 */
	public onWindowResize = (): void => {
		if (window.innerHeight >= window.innerWidth) {
			this.portraitMode = true;
		} else {
			this.portraitMode = false;
		}
	}

	/**
	 * On language change
	 */
	public onLanguageChange(language: Language): void {
		this.i18n.setLanguage(language);
	}

	/**
	 * On selected option change
	 */
	public onSelectedOptionChange(selectedOption?: number | null, closeMenu = true): void {
		this.selectedOption = selectedOption;
		this.selectedOptionInfo = {
			selected: selectedOption
		};

		if (this.selectedOption != null) {
			this.selectedOptionContent = this.siteContent?.find(sc => sc.id === this.selectedOption);
		} else {
			this.selectedOptionContent = null;
		}

		if (closeMenu) {
			this.portraitModeMenuOpened = false;
		}

		clearTimeout(this.selectedOptionTimeout);

		this.selectedOptionTimeout = setTimeout(() => {
			this.selectedOptionInfo = {
				selected: null
			};
			this.selectedOptionContent = null;
			this.selectedOption = null;
		}, MINUTES_TO_CLOSE * 60000);
	}

	/**
	 * On highlighted option change
	 */
	public onHighlightedOptionChange(option?: number | null): void {
		this.highlightedOption = option;
	}

	/**
	 * Toggle portrait mode Menu
	 */
	public togglePortraitModeMenu(): void {
		this.onSelectedOptionChange(null, false);
		this.portraitModeMenuOpened = !this.portraitModeMenuOpened;
	}

	/**
	 * Open Brand Dialog
	 */
	public openBrandDialog(): void {
		this.dialog.openDialog(this.i18n._.BRAND, this.i18n._.BRAND_TEXT);
	}

	/**
	 * Open Concept Dialog
	 */
	public openConceptDialog(): void {
		this.dialog.openDialog(this.i18n._.CONCEPT, this.i18n._.CONCEPT_TEXT);
	}

	/**
	 * Open Cookies Popup
	 */
	public openCookiesPopup(): void {
		this.dialog.openPopup({
			text: this.i18n._.COOKIES_MESSAGE,
			buttons: [
				{
					text: this.i18n._.ACCEPT,
					color: '#326c52',
					action: null
				},
				{
					text: this.i18n._.PRIVACY_POLICY,
					color: '#385a62',
					action: () => {
						this.footer?.openPrivacyDialog();
						return true;
					}
				}
			]
		});
	}
}
